/* 작품 추천 링크 — 작가가 자기 작품 링크를 복사해 퍼뜨리면, 그 링크로 들어온 이용자의 이용분은 작가 정산을 더 쳐 준다(기획).
   링크 = 작품 상세 + ?ref=작가코드. 작가코드는 서버가 서명해 발급한다(GET /creator/me/referral/ → {code}, 2026-10-13 연동).
   예전엔 계정 id 로 만든 임시 코드를 붙여, 그 링크로 대화를 시작하면 서버가 “작가 추천 코드를 확인해 주세요”로 거절했다. */
import { api } from '@/features/veluvit/services/api'
import { WORK_DRAFT_ID } from '@/features/studio-renew/lib/work-draft'
import { useRenewStore } from '@/features/studio-renew/store'
import { flushAll, studioServerId } from '@/features/studio-renew/sync'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type { Chat } from '@/features/studio-renew/types'

/** 작가코드는 계정마다 늘 같은 값이라 한 번 받아 둔다 — 실패하면 기억을 지워 다음 복사 때 다시 받는다(자동 재요청 없음) */
let cached: { owner: string; code: Promise<string> } | null = null
function creatorCode() {
  const owner = useRenewStore.getState().owner || ''
  if (!cached || cached.owner !== owner) {
    const code = api<{ code: string }>('/creator/me/referral/').then(
      (r) => r.code
    )
    cached = { owner, code }
    code.catch(() => {
      if (cached?.code === code) cached = null
    })
  }
  return cached.code
}

/** 서버가 접근 가능하다고 확인한 public/link 작품만 공유한다. 홈 승인 상태는 링크 접근과 별개다. */
export const canShare = (chat: Chat) =>
  chat.publication?.published === true &&
  (chat.publication.visibility === 'public' ||
    chat.publication.visibility === 'link')

/** 로컬 비공개 변경은 즉시 가리고, shareable 여부는 서버 접근 DTO로 판정한다. */
export const canCopyProductLink = (chat: Chat) =>
  canShare(chat) && chat.visibility !== 'private'

export function publicationLabel(chat: Chat): string {
  const publication = chat.publication
  if (publication) {
    if (!publication.published) {
      if (publication.visibility === 'private') return '비공개'
      if (publication.visibility === 'link') return '링크 공개 대기'
      if (chat.internalReview?.status === 'submitted')
        return '전체 공개 대기 · 홈 검수 중'
      return '전체 공개 대기'
    }
    let current: string
    if (publication.visibility === 'private') current = '비공개'
    else if (publication.visibility === 'link') current = '링크 공개 중'
    else if (publication.homeEligible)
      current =
        chat.internalReview?.status === 'submitted'
          ? '홈 공개 중 · 수정 검수 중'
          : '홈 공개 중'
    else if (chat.internalReview?.status === 'submitted')
      current = '전체 공개 중 · 홈 검수 중'
    else if (chat.internalReview?.status === 'rejected')
      current = '전체 공개 중 · 홈 검수 반려'
    else current = '전체 공개 중 · 홈 미노출'
    if (chat.visibility !== publication.visibility)
      current += ' · 공개 설정 저장 중'
    return current
  }
  if (chat.visibility === 'private') return '비공개 설정'
  if (chat.visibility === 'link') return '링크 공개 대기'
  if (chat.internalReview?.status === 'submitted')
    return '전체 공개 대기 · 홈 검수 중'
  return '전체 공개 설정'
}

export function publicationHint(chat: Chat): string | undefined {
  const publication = chat.publication
  if (!publication?.published) return undefined
  if (publication.visibility === 'private')
    return '비공개 작품은 링크와 검색에서 열리지 않아요.'
  if (publication.visibility === 'link')
    return '링크가 있는 사람만 볼 수 있어요. 검색과 홈에는 노출되지 않아요.'
  if (publication.homeEligible && chat.internalReview?.status === 'submitted')
    return '링크와 검색에서 볼 수 있고, 홈에는 심사를 마친 내용이 노출돼요. 현재 수정본은 검수 후 반영돼요.'
  if (publication.homeEligible)
    return '링크와 검색에서 볼 수 있고, 홈에는 내부 심사를 마친 내용이 노출돼요.'
  if (chat.internalReview?.status === 'submitted')
    return '링크와 검색에서 볼 수 있어요. 홈에는 내부 심사를 마친 뒤 노출돼요.'
  if (chat.internalReview?.status === 'rejected')
    return '링크와 검색에서 볼 수 있어요. 홈 노출은 내부 심사 승인 후 가능해요.'
  return '링크와 검색에서 볼 수 있지만 홈에는 노출되지 않아요.'
}

/** 작품 링크 — 서버 작품 id 와 서버가 발급한 작가코드로 만든다 */
export async function productLink(chat: Chat) {
  const owner = useRenewStore.getState().owner
  const identity = useSessionStore.getState().identity
  if (!owner || chat.id === WORK_DRAFT_ID)
    throw new Error('저장된 작품만 링크를 만들 수 있어요.')
  const sameOwner = () =>
    useRenewStore.getState().owner === owner &&
    useSessionStore.getState().identity === identity
  await flushAll([`chat:${chat.id}`])
  if (!sameOwner())
    throw new Error('계정이 바뀌어 작품 링크를 만들지 않았어요.')
  const latest = useRenewStore.getState()
  const current = latest.chats[chat.id]
  if (latest.owner !== owner || !current || !canCopyProductLink(current))
    throw new Error('서버에서 공개 중인 작품만 링크를 복사할 수 있어요.')
  const workId = studioServerId('chat', current.id)
  if (!workId || workId === WORK_DRAFT_ID)
    throw new Error('서버에 저장된 작품 ID를 확인하지 못했어요.')
  const code = await creatorCode()
  const afterCode = useRenewStore.getState()
  const latestAfterCode = afterCode.chats[chat.id]
  if (
    !sameOwner() ||
    afterCode.owner !== owner ||
    !latestAfterCode ||
    !canCopyProductLink(latestAfterCode) ||
    studioServerId('chat', latestAfterCode.id) !== workId
  ) {
    throw new Error('작품 공개 상태가 바뀌었어요. 저장 후 다시 시도해 주세요.')
  }
  return `${location.origin}/c/${workId}?ref=${encodeURIComponent(code)}`
}
